import { Add, Edit } from "@mui/icons-material";
import { Button, IconButton, Switch, Tooltip } from "@mui/material";
import { useFormik } from "formik";
import moment from "moment";
import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { API_URLS } from "../../config/APIUrls";
import axiosInstance from "../../config/axios";
import CustomDialog from "../../Shared/CustomDialogBox";
import CustomFilter from "../../Shared/CustomForFiler";
import CustomTable from "../../Shared/CustomTable";
import CustomToPagination from "../../Shared/CustomToPagination";

const QualificationMaster = () => {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [page, setPage] = useState(1);

    const filterFormik = useFormik({
        initialValues: { search: "", start_date: "", end_date: "" },
        enableReinitialize: true,
    });

    const { data: qualificationData, isLoading: loading } = useQuery(
        ["get_qualification_master", page, filterFormik.values],
        () =>
            axiosInstance.get(API_URLS.get_qualification_master, {
                params: {
                    search: filterFormik.values.search,
                    start_date: filterFormik.values.start_date,
                    end_date: filterFormik.values.end_date,
                    page,
                    count: 10,
                },
            }),
        { refetchOnWindowFocus: false }
    );

    const qualificationList = qualificationData?.data?.response || [];

    const fk = useFormik({
        initialValues: {
            qualification: "",
            description: "N/A",
            status: "1",
        },
        onSubmit: async (values) => {
            if (editData) return updateQualification(values);
            return createQualification(values);
        },
    });

    // CREATE
    const createQualification = async (values) => {
        try {
            const reqBody = {
                m09_ql_qualification: values.qualification,
                m09_ql_des: values.description,
                m09_ql_status: "1",
            };

            const res = await axiosInstance.post(API_URLS.create_qualification, reqBody);

            toast(res?.data?.msg);
            if (res?.data?.success) {
                queryClient.invalidateQueries("get_qualification_master");
                handleClose();
            }
        } catch {
            toast.error("Failed to create qualification");
        }
    };

    // UPDATE
    const updateQualification = async (values) => {
        try {
            const reqBody = {
                m09_ql_id: editData.m09_ql_id,
                m09_ql_qualification: values.qualification,
                m09_ql_des: values.description,
                m09_ql_status: values.status,
            };

            const res = await axiosInstance.post(API_URLS.update_qualification, reqBody);

            toast(res?.data?.msg);
            if (res?.data?.success) {
                queryClient.invalidateQueries("get_qualification_master");
                handleClose();
            }
        } catch {
            toast.error("Failed to update qualification");
        }
    };

    // STATUS UPDATE
    const toggleStatus = async (row, checked) => {
        try {
            const reqBody = {
                m09_ql_id: row.m09_ql_id,
                m09_ql_status: checked ? 1 : 0,
            };

            const res = await axiosInstance.post(
                API_URLS.update_qualification_status,
                reqBody
            );

            toast(res?.data?.msg);
            if (res?.data?.success)
                queryClient.invalidateQueries("get_qualification_master");
        } catch {
            toast.error("Failed to update status");
        }
    };



    const handleExport = async () => {
        try {
            const res = await axiosInstance.get(API_URLS.qualificatoin_status_excel, {
                search: filterFormik.values.search || "",
                start_date: filterFormik.values.start_date || "",
                end_date: filterFormik.values.end_date || "",
            })
            const fileUrl = res?.data?.response?.url;
            window.open(fileUrl, "_blank")
        }
        catch (e) {
            console.log("Something went wrong")
        }
    }

    // Dialog control
    const handleOpen = () => {
        fk.resetForm();
        setEditData(null);
        setOpen(true);
    };

    const handleEdit = (row) => {
        setEditData(row);
        fk.setValues({
            qualification: row.m09_ql_qualification,
            description: row.m09_ql_des,
            status: String(row.m09_ql_status),
        });
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    // TABLE HEADERS
    const tableHead = [
        "S.No",
        "Qualification",
        "Description",
        "Status",
        "Actions",
        "Created Date",
    ];

    const tableRows = qualificationList?.data?.map((item, index) => [
        index + 1,
        item.m09_ql_qualification || "--",
        item.m09_ql_des || "--",
        <Switch
            checked={item.m09_ql_status === 1}
            onChange={(e) => toggleStatus(item, e.target.checked)}
            color="success"
        />,
        <IconButton onClick={() => handleEdit(item)}>
            <Edit className="text-blue-600" />
        </IconButton>,
        moment(item.m09_ql_created_at).format("DD-MM-YYYY"),
    ]);

    return (
        <div className="mx-5">
            <div className="flex justify-between items-center p-1 mb-5">
                <p className="font-bold text-xl">Qualification Master</p>


                <Button variant="contained" className="!bg-[#00008b]"
                    startIcon={<Add />}
                    onClick={handleOpen}
                >
                    Add 
                </Button>
            </div>

            <CustomFilter
                formik={filterFormik}
                onFilter={() => {
                    setPage(1);
                    queryClient.invalidateQueries("get_qualification_master");
                }}
                onClear={() => {
                    filterFormik.resetForm();
                    setPage(1);
                    queryClient.invalidateQueries("get_qualification_master");
                }}
                onExport={handleExport}
            />

            <CustomTable
                tablehead={tableHead}
                tablerow={tableRows}
                isLoading={loading}
            />

            <CustomToPagination
                setPage={setPage}
                page={page}
                data={qualificationList}
            />

            <CustomDialog
                open={open}
                onClose={handleClose}
                onSubmit={fk.handleSubmit}
                title={editData ? "Edit Qualification" : "Add Qualification"}
                formik={fk}
                fields={[
                    { name: "qualification", label: "Qualification" },
                    { name: "description", label: "Description" },
                ]}
            />
        </div>
    );
};

export default QualificationMaster;
